import { useContext, useMemo } from 'react'
import { AppContext, AppContextType } from '../../AppContext'
import ElectronIPC from '../../services/ElectronIPC'
import { env } from '../../services/EnvironmentVariables'
import { persistent } from '../../services/LocalStorage'
import { logger } from '../../services/Logger'
import { getAudioAndVideoMediaSources, getDesktopSources, getVideoStreamFromId } from '../../services/MediaDevices'
import useUserContextRef from '../../services/useUserContextRef'
import { MediaPermissions, UserContext, UserContextType } from '../../UserContext'
import { LocalVideoTrack } from 'livekit-client'

const log = logger.createChildLogger({ name: 'MediaPermissionActions' })

export default function useMediaPermissionActions() {
    const { dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const { userStateRef } = useUserContextRef()

    return useMemo(() => {
        function areAllPermissionsGood() {
            for (let k of Object.keys(userStateRef.current.mediaPermissions)) {
                // @ts-ignore
                let p = userStateRef.current.mediaPermissions[k]
                if (p === false) {
                    return false
                }
            }
            return true
        }

        function showDialog() {
            appDispatch({
                type: 'update_app_state',
                value: {
                    showMediaPermissionsDialog: true,
                },
            })
        }

        async function showMediaPermissionsChecklistIfNeeded() {
            if (env.CLEAR_DO_NOT_SHOW_MEDIA_PERMISSION_CHECKLIST) {
                persistent.delete('donotshow.MediaPermissionsChecklistModal')
            }

            if (env.ALWAYS_SHOW_MEDIA_PERMISSION_CHECKLIST) {
                showDialog()
                return
            }

            if (!persistent.getBoolean('donotshow.MediaPermissionsChecklistModal')) {
                if (env.OS === 'darwin' && env.IS_ELECTRON) {
                    showDialog()
                } else {
                    log.verbose('showMediaPermissionsChecklistIfNeeded', 'os is not darwin so not showing dialog.')
                }
            } else {
                log.verbose(
                    'showMediaPermissionsChecklistIfNeeded',
                    '"donotshow.MediaPermissionsChecklistModal" set so not showing dialog.'
                )
            }
        }

        function closeMediaPermissionsChecklist() {
            appDispatch({
                type: 'update_app_state',
                value: {
                    showMediaPermissionsDialog: false,
                },
            })
        }

        async function getCurrentMediaPermissions() {
            const r: MediaPermissions = {
                audio: (await ElectronIPC.getMediaAccessStatus('microphone')) === 'granted',
                video: (await ElectronIPC.getMediaAccessStatus('camera')) === 'granted',
                desktop: (await ElectronIPC.getMediaAccessStatus('screen')) === 'granted',
                midi: true,
                // calls `systemPreferences.isTrustedAccessibilityClient()` behind the scenes.
                accessibility: (await ElectronIPC.getMediaAccessStatus('accessibility')) === 'granted',
            }
            return r
        }

        async function loadCurrentMediaPermissions() {
            const r = await getCurrentMediaPermissions()

            log.checkpoint('media_permissions', '', { status: r })

            // update `userStateRef.current.mediaPermissions` so that we can immediately
            // use it.
            for (let k of Object.keys(r)) {
                // @ts-ignore
                userStateRef.current.mediaPermissions[k] = r[k]
            }

            dispatch({
                type: 'update_media_permissions',
                value: r,
            })
        }

        async function askForInitialMediaAccess() {
            const permissions = userStateRef.current.mediaPermissions // await getCurrentMediaPermissions()

            if (!permissions.video || !permissions.audio) {
                const r = await getAudioAndVideoMediaSources({
                    origin: 'askForInitialMediaAccess',
                    forceShowAllAudioDevices: false,
                })
                if (!permissions.video) {
                    if (r.video.length > 0) {
                        try {
                            const stream = await getVideoStreamFromId(r.video[0].deviceId)
                            if (stream instanceof LocalVideoTrack) {
                                stream.stop()
                            }
                        } catch (e) {}
                    }
                }
                if (!permissions.audio) {
                    if (r.audioInputs.length > 0) {
                        try {
                            const stream = await navigator.mediaDevices.getUserMedia({
                                audio: {
                                    deviceId: { exact: r.audioInputs[0].deviceId },
                                },
                            })
                            stream.stop()
                        } catch (e) {}
                    }
                }
            }

            if (!permissions.desktop) {
                await getDesktopSources()
            }

            if (!permissions.accessibility) {
                ElectronIPC.askForMediaAccess('accessibility')
            }
        }

        function openPrivacyPreferencesSettings(mediaType: 'microphone' | 'camera' | 'screen' | 'accessibility') {
            if (mediaType === 'camera') {
                ElectronIPC.openMacSystemPreferences({ pane: 'security', section: 'Privacy_Camera' })
            } else if (mediaType === 'screen') {
                ElectronIPC.openMacSystemPreferences({ pane: 'security', section: 'Privacy_ScreenCapture' })
            } else if (mediaType === 'microphone') {
                ElectronIPC.openMacSystemPreferences({ pane: 'security', section: 'Privacy_Microphone' })
            } else if (mediaType === 'accessibility') {
                ElectronIPC.openMacSystemPreferences({ pane: 'security', section: 'Privacy_Accessibility' })
            }
        }

        return {
            showMediaPermissionsChecklistIfNeeded,
            closeMediaPermissionsChecklist,
            loadCurrentMediaPermissions,
            askForInitialMediaAccess,
            openPrivacyPreferencesSettings,
        }
    }, [])
}
